import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { _values, BadImplementationException, compare, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { DBDef_RuntimeFinding, FindingType_Enumerated, FindingType_Range, } from '@app/advisor-shared';
import { ModuleFE_BaseAdvisorRuntime } from '../../../modules/ModuleFE_BaseAdvisorRuntime.js';
import { ModuleFE_Advisor_Expression, ModuleFE_Advisor_Variable } from '../../../modules/static-modules/healthcare-space.js';
import { AdvisorConfigKeyFE_HomePanel, AdvisorConfigKeyFE_Tag_SpecialAttention } from '../../../modules/static-modules/app-config.js';
import { StorageKey, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { formatFindings } from '../../../translation-utils.js';
import { ExpressionType_NormalRange } from '@app/hcs-shared/expression';
import { ModuleFE_AdvisorSession } from '../../advisor-session/index.js';
import { sendValidateCacheMessage } from '../../../consts.js';
import { ModuleFE_EMInterface } from '../em-interface/index.js';
import { ModuleFE_EncounterMetadata } from '../../encounter-matadata/ModuleFE_EncounterMetadata.js';
export const dispatch_onRuntimeFindingsUpdated = new ThunderDispatcherV3('__onRuntimeFindingsUpdated');
export const dispatch_onChiefComplaintsChanged = new ThunderDispatcher('__onChiefComplaintsChanged');
export class ModuleFE_RuntimeFinding_Class extends ModuleFE_BaseAdvisorRuntime {
    _CCReports;
    constructor() {
        super(DBDef_RuntimeFinding, dispatch_onRuntimeFindingsUpdated);
    }
    __onAdvisorReset = () => {
        this._CCReports = undefined;
    };
    __onRuntimeFindingsUpdated = (..._params) => {
        this.syncChiefComplaints();
    };
    init() {
        super.init();
        this.setCacheFilter(ModuleFE_AdvisorSession.patientFilter);
    }
    __validateCache() {
        const patientId = ModuleFE_AdvisorSession.getCurrentPatientId();
        if (!patientId)
            return;
        const irrelevantFindings = this.cache.filter(finding => finding.patientId !== patientId);
        const moduleName = this.getName();
        if (!irrelevantFindings.length)
            return this.logDebug(`${moduleName} cache is valid`);
        this.logErrorBold(`${moduleName} has invalid data in cache`, irrelevantFindings);
        return sendValidateCacheMessage(irrelevantFindings, moduleName);
    }
    getSubFindings = (finding) => {
        return this.cache.filter(i => i.parentId === finding._id);
    };
    getAllFindingsToRender = (onlyAbnormalResults, ignoreHideNegativeConfig) => {
        const hideNegative = new StorageKey('storage-key__hide-negative').get(true);
        return this.cache.filter(finding => {
            if (finding.isGeneratedVariable)
                return false;
            if (finding.type === FindingType_Range)
                return false;
            if (!ignoreHideNegativeConfig && hideNegative && finding.type === FindingType_Enumerated)
                return !ModuleFE_EMInterface.isNegativeValue(finding.valueIds[0]);
            if (onlyAbnormalResults && finding.type === 'numeric')
                return this.findingIsAbnormal(finding);
            return true;
        });
    };
    findingIsAbnormal = (finding) => {
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable)
            return false;
        //Calculate finding normal range
        const varExpression = ModuleFE_Advisor_Expression.cache.find(exp => exp.associatedVar === variable._id
            && exp.expressionType === ExpressionType_NormalRange.dbKey);
        const expressionFindings = this.cache.filter(finding => finding.variableId === varExpression?._id);
        const latestExpressionFinding = sortArray(expressionFindings, finding => finding.__created, true)[0];
        const normalRange = latestExpressionFinding?.value;
        if (!normalRange)
            return false;
        return finding.value < normalRange[0] || finding.value > normalRange[1];
    };
    getSpecialAttention = (_variable) => {
        const variable = typeof _variable === 'string' ? ModuleFE_Advisor_Variable.cache.unique(_variable) : _variable;
        if (!variable)
            throw new MUSTNeverHappenException(`Could not resolve variable for data ${_variable}`);
        const answers = { disease: false };
        const specialAttention = AdvisorConfigKeyFE_Tag_SpecialAttention.get();
        const specialDiseaseTagId = specialAttention?.disease;
        if (specialDiseaseTagId && variable.tagIds.some(group => group.value.includes(specialDiseaseTagId)))
            answers.disease = true;
        return answers;
    };
    getLatestFindings(asVariableIdMap, filterPreviousEncounters) {
        const map = {};
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        const allFindings = this.cache.filter(finding => {
            if (filterPreviousEncounters && finding.encounterId !== currentEncounterId)
                return false;
            return !finding.isGeneratedVariable;
        });
        allFindings.forEach(f => {
            if (!map[f.variableId] || map[f.variableId].__created < f.__created)
                map[f.variableId] = f;
        });
        return asVariableIdMap ? map : _values(map);
    }
    async clearEncounterDataFromIDB(encounterId) {
        const patientEncounter = await ModuleFE_EncounterMetadata.IDB.unique(encounterId);
        if (!patientEncounter)
            throw new BadImplementationException('Failed to get patientEncounter to clean from IDB');
        const runtimeDataToDelete = this.cache.filter(finding => finding.patientId === patientEncounter.patientId);
        await this.IDB.storeWrapper.deleteAll(runtimeDataToDelete);
    }
    async clearEncounterData(encounterId, clearSelf = false) {
        await super.clearEncounterData(encounterId, clearSelf);
        this.syncChiefComplaints();
    }
    toggleChiefComplaint = async (finding) => {
        const processed = {
            findingId: finding._id,
            isChiefComplaint: !finding.isChiefComplaint,
            snippetId: finding.snippetId,
            type: finding.type,
            valueIds: finding.valueIds,
            variableId: finding.variableId,
            attributes: finding.attributes,
            parentId: finding.parentId,
        };
        return ModuleFE_EMInterface.updateEncounterApis.updateNeededVariables({ findings: formatFindings([processed]) });
    };
    getFindingResultsForVariable = (variable) => {
        const findings = this.cache.filter(f => f.variableId === variable._id);
        sortArray(findings, f => f.issuedTimestamp);
        return { variable, findings };
    };
    getLatestFindingForVarId = (varId) => {
        const findings = this.cache.filter(f => f.variableId === varId);
        sortArray(findings, f => f.issuedTimestamp, true);
        return findings[0];
    };
    getChiefComplaints = () => {
        return [...(this._CCReports ?? this.collectChiefComplaints())];
    };
    collectChiefComplaints = () => {
        const ccDescriptionId = AdvisorConfigKeyFE_HomePanel.get()?.ccVariableId;
        const chiefComplaints = this.getLatestFindings(false, true).filter(finding => finding.isChiefComplaint);
        const reports = chiefComplaints.reduce((acc, finding) => {
            const existing = acc.find(report => report.variableId === finding.variableId);
            if (existing || finding.variableId === ccDescriptionId)
                return acc;
            const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
            if (!variable)
                return acc;
            acc.push({ findingId: finding._id, variableId: variable._id, label: variable.name, timestamp: finding.issuedTimestamp });
            return acc;
        }, []);
        sortArray(sortArray(reports, 'variableId'), report => report.timestamp);
        return reports;
    };
    syncChiefComplaints = () => {
        const prevReports = this._CCReports;
        const reports = this.collectChiefComplaints();
        if (compare(prevReports, reports)) {
            this._CCReports = reports;
            return;
        }
        this._CCReports = reports;
        dispatch_onChiefComplaintsChanged.dispatchAll(prevReports, [...reports]);
    };
}
export const ModuleFE_RuntimeFinding = new ModuleFE_RuntimeFinding_Class();
//# sourceMappingURL=ModuleFE_RuntimeFinding.js.map